import { OrderSearchParams } from "../types";

type OrderSearchFieldType = "string" | "number" | "boolean" | "string[]";

/**
 * Single source of truth for every filterable field on OrderSearchParams.
 * Drives hasActiveOrderSearchParams, serializeOrderSearchParams, and
 * parseOrderSearchParamsFromUrl below — add a field here once and all three
 * stay in sync, instead of hand-editing a separate field list in each place
 * (which is how the channel/delivery/gift filters previously ended up wired
 * into search but silently dropped by the statistics page's URL parsing).
 */
const ORDER_SEARCH_FIELD_TYPES: Partial<
    Record<keyof OrderSearchParams, OrderSearchFieldType>
> = {
    search_query: "string",
    status: "string",
    payment_status: "string",
    customer_id: "string",
    customer_ids: "string[]",
    created_by_user_id: "string",
    product_id: "string",
    product_ids: "string[]",
    order_channel_id: "string",
    order_channel_type: "string",
    delivery_service_id: "string",
    delivery_service_type: "string",
    has_gift_items: "boolean",
    order_date_from: "string",
    order_date_to: "string",
    shipped_date_from: "string",
    shipped_date_to: "string",
    delivered_date_from: "string",
    delivered_date_to: "string",
    total_amount_min: "number",
    total_amount_max: "number",
};

const isFieldSet = (value: unknown, type: OrderSearchFieldType): boolean => {
    if (value === undefined || value === null) return false;
    switch (type) {
        case "string":
            return value !== "";
        case "string[]":
            return Array.isArray(value) && value.length > 0;
        case "number":
        case "boolean":
            return true;
        default:
            return false;
    }
};

/** True if any filter field (beyond pagination/sorting) is set. */
export function hasActiveOrderSearchParams(params: OrderSearchParams): boolean {
    return (Object.keys(ORDER_SEARCH_FIELD_TYPES) as (keyof OrderSearchParams)[]).some(
        (key) => isFieldSet(params[key], ORDER_SEARCH_FIELD_TYPES[key]!),
    );
}

/** Serialize the active filter fields into a URLSearchParams (e.g. for deep-linking the stats page). */
export function serializeOrderSearchParams(
    params: OrderSearchParams,
): URLSearchParams {
    const query = new URLSearchParams();
    (Object.keys(ORDER_SEARCH_FIELD_TYPES) as (keyof OrderSearchParams)[]).forEach(
        (key) => {
            const type = ORDER_SEARCH_FIELD_TYPES[key]!;
            const value = params[key];
            if (!isFieldSet(value, type)) return;
            query.set(key, Array.isArray(value) ? value.join(",") : String(value));
        },
    );
    return query;
}

/** The All Orders page, filtered to one customer's orders. */
export function customerOrdersUrl(customerId: string): string {
    return `/orders?${serializeOrderSearchParams({ customer_id: customerId })}`;
}

/** Inverse of serializeOrderSearchParams — parse filter fields back out of a URLSearchParams. */
export function parseOrderSearchParamsFromUrl(
    urlParams: URLSearchParams,
): OrderSearchParams {
    const params: OrderSearchParams = {};
    (Object.keys(ORDER_SEARCH_FIELD_TYPES) as (keyof OrderSearchParams)[]).forEach(
        (key) => {
            const raw = urlParams.get(key);
            if (raw === null || raw === "") return;
            const type = ORDER_SEARCH_FIELD_TYPES[key]!;
            switch (type) {
                case "string":
                    (params as any)[key] = raw;
                    break;
                case "number":
                    (params as any)[key] = parseFloat(raw);
                    break;
                case "boolean":
                    (params as any)[key] = raw === "true";
                    break;
                case "string[]":
                    (params as any)[key] = raw.split(",").filter(Boolean);
                    break;
            }
        },
    );
    return params;
}
